import { useState, useCallback, useEffect } from 'react';
import {
  getFullscreenElement,
  isElementFullscreen,
  requestFullscreen,
  exitFullscreen,
} from '../utils/fullscreenApi';

/**
 * Native fullscreen for a container element. Syncs via fullscreenchange.
 */
export function useFullscreen(containerRef) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const sync = useCallback(() => {
    const el = containerRef?.current;
    setIsFullscreen(el ? isElementFullscreen(el) : false);
  }, [containerRef]);

  useEffect(() => {
    const events = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
    events.forEach((name) => document.addEventListener(name, sync));
    sync();
    return () => events.forEach((name) => document.removeEventListener(name, sync));
  }, [sync]);

  const enterFullscreen = useCallback(async () => {
    const el = containerRef?.current;
    if (!el || isElementFullscreen(el)) return;
    await requestFullscreen(el);
    sync();
  }, [containerRef, sync]);

  const exitFullscreenMode = useCallback(async () => {
    if (!getFullscreenElement()) return;
    await exitFullscreen();
    sync();
  }, [sync]);

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef?.current;
    if (!el) return;
    if (isElementFullscreen(el)) {
      await exitFullscreenMode();
    } else {
      await enterFullscreen();
    }
  }, [containerRef, enterFullscreen, exitFullscreenMode]);

  return {
    isFullscreen,
    enterFullscreen,
    exitFullscreen: exitFullscreenMode,
    toggleFullscreen,
  };
}
